import type { Stats } from 'fs';

import chokidar, { type FSWatcher } from 'chokidar';
import type { Logger } from 'pino';

export type WatchEvent = 'add' | 'change' | 'unlink' | 'unlinkDir' | 'addDir';

export interface WatcherHandlers {
  onEvent(
    event: WatchEvent,
    absPath: string,
    stats?: Stats,
  ): Promise<void> | void;
  onReady?(): void;
}

export interface WatcherOptions {
  rootPath: string;
  ignored: (path: string, stats?: Stats) => boolean;
  logger: Logger;
  handlers: WatcherHandlers;
}

async function createChokidarWatcher(
  options: WatcherOptions,
  usePolling: boolean,
): Promise<{ watcher: FSWatcher; mode: 'native' | 'polling' }> {
  const log = options.logger.child({
    scope: 'watcher',
    mode: usePolling ? 'polling' : 'native',
  });
  const watcher = chokidar.watch(options.rootPath, {
    ignored: options.ignored,
    ignoreInitial: true,
    persistent: true,
    awaitWriteFinish: {
      stabilityThreshold: 1_500,
      pollInterval: 100,
    },
    atomic: true,
    usePolling,
    interval: usePolling ? 200 : undefined,
    binaryInterval: usePolling ? 200 : undefined,
    alwaysStat: true,
    cwd: undefined,
    depth: undefined,
  });

  await new Promise<void>((resolve, reject) => {
    const onReady = () => {
      watcher.off('error', onError);
      log.info('Watcher ready');
      resolve();
    };
    const onError = (err: unknown) => {
      watcher.off('ready', onReady);
      reject(err);
    };
    watcher.once('ready', onReady);
    watcher.once('error', onError);
  });

  return { watcher, mode: usePolling ? 'polling' : 'native' };
}

function attachHandlers(watcher: FSWatcher, options: WatcherOptions): void {
  const { handlers, logger } = options;

  const handle = (event: WatchEvent, filePath: string, stats?: Stats) => {
    logger.debug({ event, filePath }, 'Watcher raw event');
    Promise.resolve(handlers.onEvent(event, filePath, stats)).catch((error) => {
      logger.error(
        { err: error, event, path: filePath },
        'Watcher handler failed',
      );
    });
  };

  watcher.on('add', (filePath, stats) => handle('add', filePath, stats));
  watcher.on('change', (filePath, stats) => handle('change', filePath, stats));
  watcher.on('unlink', (filePath) => handle('unlink', filePath));
  watcher.on('addDir', (dirPath) => handle('addDir', dirPath));
  watcher.on('unlinkDir', (dirPath) => handle('unlinkDir', dirPath));
  watcher.on('error', (error) => {
    logger.error({ err: error }, 'Watcher error');
  });
  if (handlers.onReady) {
    handlers.onReady();
    logger.debug('Watcher ready callback executed');
  }
}

export async function startWatcher(options: WatcherOptions): Promise<{
  watcher: FSWatcher;
  mode: 'native' | 'polling';
}> {
  try {
    const result = await createChokidarWatcher(options, false);
    attachHandlers(result.watcher, options);
    return result;
  } catch (error) {
    options.logger.warn(
      { err: error },
      'Native watcher failed, falling back to polling',
    );
    const result = await createChokidarWatcher(options, true);
    attachHandlers(result.watcher, options);
    return result;
  }
}
